import React, { useState } from 'react';
import API from '../utils/api';
import { trackLeadCaptured } from '../utils/analytics';

// Reusable email lead-capture form. Posts to /api/leads, which stores the
// lead and emails sample/personalised stories. Used by the exit-intent
// popup, the pricing page, blog posts, and community story pages.
//
// Props:
//   source       — 'exit_intent' | 'pricing_page' | 'blog' | 'community_story' | 'referral'
//   slug         — community story slug (community_story source only)
//   extraBody    — extra fields merged into the POST body (e.g. referralToken)
//   askName      — show the child-name field (default true)
//   requireName  — make the child name mandatory (community_story)
//   askAge       — show the age select (default false)
//   buttonLabel  — submit button text
//   dark         — light text styles for dark/gradient backgrounds
const LeadEmailForm = ({
  source,
  slug,
  extraBody = null,
  askName = true,
  requireName = false,
  askAge = false,
  buttonLabel = '📬 Email me the story',
  dark = false,
}) => {
  const [email, setEmail] = useState('');
  const [childName, setChildName] = useState('');
  const [age, setAge] = useState('5');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState('');
  const [error, setError] = useState('');

  const mutedColor = dark ? 'rgba(255,255,255,0.65)' : 'var(--text-soft)';
  const labelColor = dark ? '#ffffff' : 'var(--text-main)';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || loading) return;
    setError('');
    setLoading(true);
    try {
      const body = { email: email.trim(), source, ...(extraBody || {}) };
      if (slug) body.slug = slug;
      if (childName.trim()) {
        body.childName = childName.trim();
        body.age = Number(age);
      }
      const res = await API.post('/leads', body);
      setDone(res.data.message || 'Check your inbox! 🌙');
      trackLeadCaptured(source);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="d-flex align-items-center justify-content-center gap-2 p-3 rounded-3"
        style={{background:'var(--green-section)', border:'1px solid var(--border-green)'}}>
        <span style={{fontSize:'20px'}}>📬</span>
        <span style={{color:'var(--text-green)', fontSize:'14px', fontWeight:600}}>{done}</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="alert alert-danger py-2 small rounded-3 d-flex align-items-center gap-2 mb-3">
          <i className="bi bi-exclamation-circle-fill"></i>{error}
        </div>
      )}
      {/* Stacked, full-width fields with clear labels — readable in narrow
          containers (e.g. the exit-intent modal) and on every screen size. */}
      <div className="d-flex flex-column gap-3 text-start">
        {askName && (
          <div>
            <label htmlFor="lead-child-name" className="form-label small fw-semibold mb-1" style={{ color: labelColor }}>
              {requireName ? "Child's name" : "Child's name (optional)"}
            </label>
            <input
              id="lead-child-name"
              type="text"
              className="form-control form-control-lg"
              placeholder="e.g. Emma"
              maxLength={30}
              style={{ borderRadius: '14px', fontSize: '15px' }}
              value={childName}
              onChange={e => setChildName(e.target.value)}
              required={requireName}
            />
          </div>
        )}
        {askAge && (
          <div>
            <label htmlFor="lead-child-age" className="form-label small fw-semibold mb-1" style={{ color: labelColor }}>
              Child's age
            </label>
            <select
              id="lead-child-age"
              className="form-select form-select-lg"
              style={{ borderRadius: '14px', fontSize: '15px' }}
              value={age}
              onChange={e => setAge(e.target.value)}
            >
              {[1,2,3,4,5,6,7,8,9,10,11,12].map(a => (
                <option key={a} value={a}>{a} years old</option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label htmlFor="lead-email" className="form-label small fw-semibold mb-1" style={{ color: labelColor }}>
            Your email
          </label>
          <input
            id="lead-email"
            type="email"
            className="form-control form-control-lg"
            placeholder="you@example.com"
            style={{ borderRadius: '14px', fontSize: '15px' }}
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
        </div>
        <button type="submit" className="btn btn-warning btn-lg rounded-pill fw-bold w-100 mt-1"
          style={{ fontSize: '15px' }} disabled={loading || !email.trim()}>
          {loading
            ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending…</>
            : buttonLabel}
        </button>
      </div>
      <div className="mt-2 text-center" style={{color: mutedColor, fontSize:'12px'}}>
        Free · No credit card · Unsubscribe anytime
      </div>
    </form>
  );
};

export default LeadEmailForm;
